import { useCallback, useEffect, useRef, useState } from "react"
import type { ButtonStatus } from "@/shared/components/ui/button"

interface UseButtonStatusOptions {
  /** How long success/error state is shown before resetting (ms, default: 2000) */
  resetDelay?: number
}

/**
 * Manages button status lifecycle for async operations.
 *
 * Usage with TanStack Query:
 *   const { status, execute } = useButtonStatus()
 *   const mutation = useMutation({ ... })
 *
 *   <Button
 *     status={status}
 *     onClick={() => execute(mutation.mutateAsync(data))}
 *     onRetry={() => execute(mutation.mutateAsync(data))}
 *   >
 *     Save
 *   </Button>
 */
export function useButtonStatus(options: UseButtonStatusOptions = {}) {
  const { resetDelay = 2000 } = options
  const [status, setStatus] = useState<ButtonStatus>("idle")
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const reset = useCallback(() => {
    setStatus("idle")
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const execute = useCallback(
    async <T>(promise: Promise<T>): Promise<T | undefined> => {
      if (timerRef.current) clearTimeout(timerRef.current)
      setStatus("loading")

      try {
        const result = await promise
        setStatus("success")
        timerRef.current = setTimeout(() => setStatus("idle"), resetDelay)
        return result
      } catch {
        setStatus("error")
        timerRef.current = setTimeout(() => setStatus("idle"), resetDelay)
        return undefined
      }
    },
    [resetDelay]
  )

  return { status, execute, reset, setStatus }
}
